import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Sidebar,
  SidebarSimple,
  Boat,
  CaretDown,
  CaretUp,
  MagnifyingGlass,
  CheckCircle,
  SquaresFour,
  ChatsCircle,
  EnvelopeSimple,
  ArrowsOut,
  Bell,
  Clock,
  ArrowCircleRight,
  UserCircle,
  SignOut,
} from '@phosphor-icons/react';
import { MODERN_ICON } from '../../constants/modernIcons';
import constant from '../../constant';
import { Link, useNavigate } from 'react-router-dom';
import { useUserDetails } from '../../hooks/useUserDetails';
import { useCan } from '../../hooks/useCan';
import { usePermissions } from '../../context/PermissionContext';
import { useCurrentWorkingVessel } from '../../customHooks/useCurrentWorkingVessel';
import { useIsReadOnlyAdmin } from '../../hooks/useIsReadOnlyAdmin';
import { VESSEL_SELECTED_EVENT } from '../../customHooks/useResolvedVessel';
import { v2_fetch_vessels } from '../../actions/v2/vessel';
import { v2_get_notifications } from '../../actions/v2/notifications';
import { formatTimeAgo, notificationLink } from '../../utils/notificationLinks';
import SyncStatusBadge from './SyncStatusBadge';
import MaritimeThemeControl from './MaritimeThemeControl';

const SIDEBAR_COLLAPSED_KEY = 'sidebarCollapsed';

const STORAGE_KEY = constant.keys.selectedVessel;

const Header = () => {

    const { user } = useUserDetails();
    const { vessel, loading: vesselLoading } = useCurrentWorkingVessel();
    const { roleName } = usePermissions();
    const navigate = useNavigate();
    const adminPerm = useCan('superadmin.dashboard');
    const ownerPerm = useCan('owner.dashboard');
    const crewPerm = useCan('crew.dashboard');
    const { isReadOnlyAdmin } = useIsReadOnlyAdmin();
    const isCrewProfile =
        !adminPerm.canView &&
        (crewPerm.canView || String(roleName || '').toLowerCase() === 'crew');

    /** Fleet / shore roles pick vessels per page — no global header selector. */
    const hideFleetVesselSelector = useMemo(() => {
        if (isCrewProfile) return false;
        const role = String(roleName || '').toLowerCase();
        return (
            isReadOnlyAdmin ||
            /superintendent/i.test(role) ||
            /manager/i.test(role)
        );
    }, [isCrewProfile, isReadOnlyAdmin, roleName]);

    const [vessels, setVessels] = useState([]);
    const [selectedVessel, setSelectedVessel] = useState(null);
    const [vesselDropOpen, setVesselDropOpen] = useState(false);
    const [vesselSearch, setVesselSearch] = useState('');
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
        const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
        if (saved !== null) return saved === '1';
        return window.innerWidth <= 768;
    });
    const [headerNotifications, setHeaderNotifications] = useState([]);
    const [notificationBadge, setNotificationBadge] = useState(0);
    const [loadingNotifications, setLoadingNotifications] = useState(false);

    const canViewNotifications =
        adminPerm.canView || ownerPerm.canView || crewPerm.canView;

    const loadHeaderNotifications = useCallback(() => {
        if (!canViewNotifications || vesselLoading) return;
        setLoadingNotifications(true);
        v2_get_notifications({
            vessel_id: vessel?.vessel_id || undefined,
            category: 'all',
            limit: 8,
            offset: 0,
        })
            .then((res) => {
                if (res?.status) {
                    setHeaderNotifications(res.result?.rows || []);
                    setNotificationBadge(res.result?.unread_count || 0);
                }
            })
            .catch(() => {
                setHeaderNotifications([]);
                setNotificationBadge(0);
            })
            .finally(() => setLoadingNotifications(false));
    }, [canViewNotifications, isCrewProfile, vessel?.vessel_id, vesselLoading]);

    // Apply / remove the body class whenever the toggle state changes
    useEffect(() => {
        const cls = 'sidebar-collapsed';
        if (sidebarCollapsed) {
            document.body.classList.add(cls);
        } else {
            document.body.classList.remove(cls);
        }
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? '1' : '0');
    }, [sidebarCollapsed]);

    useEffect(() => {
        // Defer non-critical notification fetch so dashboard paints first
        const t = setTimeout(() => loadHeaderNotifications(), 1200);
        return () => clearTimeout(t);
    }, [loadHeaderNotifications]);

    // Load vessel list for office users who switch vessel in the header
    useEffect(() => {
        if (isCrewProfile || hideFleetVesselSelector) return;
        v2_fetch_vessels()
            .then((res) => {
                if (res.status) {
                    setVessels(res.result || []);
                    const saved = localStorage.getItem(STORAGE_KEY);
                    if (saved) {
                        try { setSelectedVessel(JSON.parse(saved)); } catch (e) { }
                    }
                }
            })
            .catch(() => setVessels([]));
    }, [isCrewProfile, hideFleetVesselSelector]);

    // Crew: lock to assigned vessel only. Office: restore from storage or assignment.
    useEffect(() => {
        if (hideFleetVesselSelector) return;
        if (!vessel?.vessel_id) return;

        if (isCrewProfile) {
            const mapped = {
                value: vessel.vessel_id,
                label: vessel.vessel_name || "My Vessel",
            };
            setSelectedVessel(mapped);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(mapped));
            return;
        }

        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            try { setSelectedVessel(JSON.parse(saved)); } catch (e) { }
        } else {
            const mapped = {
                value: vessel.vessel_id,
                label: vessel.vessel_name || "Vessel",
            };
            setSelectedVessel(mapped);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(mapped));
        }
    }, [vessel?.vessel_id, vessel?.vessel_name, isCrewProfile, hideFleetVesselSelector]);

    const handleVesselSelect = (v) => {
        setSelectedVessel(v);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(v));
        window.dispatchEvent(new CustomEvent(VESSEL_SELECTED_EVENT));
        setVesselDropOpen(false);
        setVesselSearch('');
        if (isCrewProfile) {
            navigate(constant.Authedcomponent.vesselDashboard.url, { state: { vessel_id: v.value } });
        }
    };

    const filteredVessels = vessels.filter(v =>
        v.label?.toLowerCase().includes(vesselSearch.toLowerCase())
    );

    return (
        <header id="page-topbar" style={{ backgroundColor: "#ffffff" }}>
            <div className="navbar-header">
                <div className="d-flex iship-topbar-nav">
                    {/* LOGO */}
                    <div className="navbar-brand-box">
                        <Link to="/" className="logo logo-dark d-flex align-items-center h-100">
                            <img
                                src="/img/iship-plus-logo.png"
                                alt="iSHIP+"
                                style={{ height: 36, width: 'auto', marginLeft: 18, marginRight: 18, objectFit: 'contain' }}
                            />
                        </Link>
                    </div>

                    {/* SIDEBAR TOGGLE */}
                    <button
                        type="button"
                        className="sidebar-toggle-btn"
                        onClick={() => setSidebarCollapsed((c) => !c)}
                        aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    >
                        {sidebarCollapsed
                            ? <SidebarSimple {...MODERN_ICON.lg} />
                            : <Sidebar {...MODERN_ICON.lg} />}
                    </button>
                </div>

                <div className="d-flex align-items-center iship-topbar-actions">

                    {/* ── Vessel: crew sees assigned vessel; fleet shore roles have no selector ── */}
                    {!hideFleetVesselSelector && (
                    <div
                        className="d-none d-lg-inline-block ms-2"
                        style={{ position: 'relative' }}
                    >
                        {isCrewProfile ? (
                            <span
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '7px',
                                    background: 'linear-gradient(135deg, #0f4c75 0%, #1b6ca8 100%)',
                                    color: '#ffffff',
                                    borderRadius: '8px',
                                    padding: '6px 14px',
                                    fontWeight: 600,
                                    fontSize: '13px',
                                    whiteSpace: 'nowrap',
                                    boxShadow: 'none',
                                }}
                                title="Your assigned vessel"
                            >
                                <Boat {...MODERN_ICON.sm} />
                                {selectedVessel?.label || vessel?.vessel_name || 'My Vessel'}
                            </span>
                        ) : (
                        <>
                        <button
                            id="vessel-selector-btn"
                            type="button"
                            onClick={() => { setVesselDropOpen(o => !o); setVesselSearch(''); }}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '7px',
                                background: selectedVessel
                                    ? 'linear-gradient(135deg, #0f4c75 0%, #1b6ca8 100%)'
                                    : 'linear-gradient(135deg, #e8f4fd 0%, #d0e8f7 100%)',
                                color: selectedVessel ? '#ffffff' : '#1b6ca8',
                                border: selectedVessel ? 'none' : '1.5px solid #1b6ca8',
                                borderRadius: '8px',
                                padding: '6px 14px',
                                fontWeight: 600,
                                fontSize: '13px',
                                cursor: 'pointer',
                                boxShadow: 'none',
                                whiteSpace: 'nowrap',
                                transition: 'all 0.2s ease',
                                minWidth: '160px',
                                justifyContent: 'space-between',
                            }}
                        >
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Boat {...MODERN_ICON.sm} />
                                <span style={{ maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {selectedVessel ? selectedVessel.label : 'Select Vessel'}
                                </span>
                            </span>
                            {vesselDropOpen
                                ? <CaretUp {...MODERN_ICON.sm} style={{ marginLeft: '4px' }} />
                                : <CaretDown {...MODERN_ICON.sm} style={{ marginLeft: '4px' }} />}
                        </button>

                        {/* Dropdown Panel */}
                        {vesselDropOpen && (
                            <>
                                {/* Backdrop */}
                                <div
                                    onClick={() => setVesselDropOpen(false)}
                                    style={{ position: 'fixed', inset: 0, zIndex: 1040 }}
                                />
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: 'calc(100% + 8px)',
                                        left: 0,
                                        zIndex: 1050,
                                        background: '#fff',
                                        borderRadius: '10px',
                                        boxShadow: 'none',
                                        width: '260px',
                                        overflow: 'hidden',
                                        border: '1px solid #e0ecf7',
                                    }}
                                >
                                    {/* Header */}
                                    <div style={{
                                        background: 'linear-gradient(135deg, #0f4c75 0%, #1b6ca8 100%)',
                                        padding: '10px 14px 8px',
                                        color: '#fff',
                                    }}>
                                        <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.5px', marginBottom: '6px', opacity: 0.85 }}>
                                            SELECT VESSEL
                                        </div>
                                        {/* Search */}
                                        <div style={{ position: 'relative' }}>
                                            <MagnifyingGlass
                                                {...MODERN_ICON.sm}
                                                style={{
                                                    position: 'absolute', left: '8px', top: '50%',
                                                    transform: 'translateY(-50%)', color: '#1b6ca8',
                                                }}
                                            />
                                            <input
                                                autoFocus
                                                type="text"
                                                placeholder="Search vessels..."
                                                value={vesselSearch}
                                                onChange={e => setVesselSearch(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '5px 8px 5px 28px',
                                                    borderRadius: '6px',
                                                    border: 'none',
                                                    fontSize: '12px',
                                                    outline: 'none',
                                                    background: 'rgba(255,255,255,0.95)',
                                                    color: '#1a2a3a',
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Vessel List */}
                                    <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
                                        {filteredVessels.length === 0 ? (
                                            <div style={{ padding: '16px', textAlign: 'center', color: '#8a9bb0', fontSize: '13px' }}>
                                                No vessels found
                                            </div>
                                        ) : filteredVessels.map((v) => {
                                            const isActive = selectedVessel?.value === v.value;
                                            return (
                                                <button
                                                    key={v.value}
                                                    type="button"
                                                    onClick={() => handleVesselSelect(v)}
                                                    style={{
                                                        width: '100%',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '10px',
                                                        padding: '9px 14px',
                                                        background: isActive ? '#e8f4fd' : 'transparent',
                                                        border: 'none',
                                                        borderBottom: '1px solid #f0f4f8',
                                                        cursor: 'pointer',
                                                        textAlign: 'left',
                                                        transition: 'background 0.15s',
                                                    }}
                                                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#f5faff'; }}
                                                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                                                >
                                                    <div style={{
                                                        width: '30px', height: '30px', borderRadius: '50%', flexShrink: 0,
                                                        background: isActive ? '#1b6ca8' : '#e0ecf7',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    }}>
                                                        <Boat
                                                            {...MODERN_ICON.sm}
                                                            style={{ color: isActive ? '#fff' : '#1b6ca8' }}
                                                        />
                                                    </div>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{
                                                            fontSize: '13px', fontWeight: isActive ? 700 : 500,
                                                            color: isActive ? '#0f4c75' : '#2a3a4a',
                                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                        }}>
                                                            {v.label}
                                                        </div>
                                                    </div>
                                                    {isActive && (
                                                        <CheckCircle {...MODERN_ICON.sm} style={{ color: '#1b6ca8', flexShrink: 0 }} />
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {/* Footer — office users only (crew has no vessel dropdown) */}
                                    {selectedVessel && !isCrewProfile && (
                                        <div style={{ padding: '8px 14px', borderTop: '1px solid #e0ecf7', background: '#f8fbff' }}>
                                            <Link
                                                to={constant.Authedcomponent.vesselDashboard.url}
                                                state={{ vessel_id: selectedVessel.value }}
                                                onClick={() => setVesselDropOpen(false)}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: '6px',
                                                    color: '#1b6ca8', fontSize: '12px', fontWeight: 600,
                                                    textDecoration: 'none',
                                                }}
                                            >
                                                <SquaresFour {...MODERN_ICON.sm} />
                                                Go to Vessel Dashboard
                                            </Link>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                        </>
                        )}
                    </div>
                    )}

                    {/* ── Vessel Wall + Check Mail ── */}
                    <div className="d-none d-lg-flex align-items-center ms-2 gap-1 topbar-actions">
                        <Link
                            to={constant.Authedcomponent.vesselWall.url}
                            className="btn vessel-wall-btn waves-effect d-inline-flex align-items-center"
                        >
                            <ChatsCircle {...MODERN_ICON.sm} className="align-middle me-1" />
                            Vessel Wall
                        </Link>
                        <Link
                            to={constant.Authedcomponent.notificationsCenter.url}
                            className="btn check-mail-btn waves-effect d-inline-flex align-items-center"
                            title="Open notifications inbox"
                        >
                            <EnvelopeSimple {...MODERN_ICON.sm} className="align-middle me-1" />
                            Check Mail
                        </Link>
                    </div>

                    {/* ── Sync / offline status ── */}
                    <div className="d-none d-md-inline-block ms-1">
                        <SyncStatusBadge />
                    </div>

                    {/* Contextual iShip Intelligence mounts here when available. */}
                    <div
                        id="iship-agent-launcher-slot"
                        className="iship-agent-launcher-slot"
                        data-agent-ignore
                    />

                    <div className="d-none d-xl-inline-block ms-1">
                        <MaritimeThemeControl />
                    </div>

                    {/* ── Fullscreen ── */}
                    <div className="dropdown d-none d-lg-inline-block ms-1">
                        <button type="button" className="btn header-item noti-icon waves-effect" data-bs-toggle="fullscreen">
                            <ArrowsOut {...MODERN_ICON.header} style={{ color: "#2a5a7b" }} />
                        </button>
                    </div>

                    {/* ── Notifications ── */}
                    <div className="dropdown d-inline-block">
                        <button
                            type="button"
                            className="btn header-item noti-icon waves-effect"
                            id="page-header-notifications-dropdown"
                            data-bs-toggle="dropdown"
                            aria-haspopup="true"
                            aria-expanded="false"
                        >
                            <Bell {...MODERN_ICON.header} style={{ color: "#2a5a7b" }} />
                            {notificationBadge > 0 ? (
                                <span className="badge bg-danger rounded-pill">
                                    {notificationBadge > 99 ? "99+" : notificationBadge}
                                </span>
                            ) : null}
                        </button>
                        <div className="dropdown-menu dropdown-menu-lg dropdown-menu-end p-0" aria-labelledby="page-header-notifications-dropdown">
                            <div className="p-3">
                                <div className="row align-items-center">
                                    <div className="col">
                                        <h6 className="m-0" key="t-notifications"> Notifications </h6>
                                    </div>
                                    <div className="col-auto">
                                        <Link to={constant.Authedcomponent.notificationsCenter.url} className="small" key="t-view-all">
                                            View All
                                        </Link>
                                    </div>
                                </div>
                            </div>
                            <div style={{ maxHeight: 230, overflowY: "auto" }}>
                                {loadingNotifications ? (
                                    <div className="px-3 py-4 text-muted small text-center">Loading…</div>
                                ) : headerNotifications.length === 0 ? (
                                    <div className="px-3 py-4 text-muted small text-center">No notifications</div>
                                ) : (
                                    headerNotifications.map((item) => (
                                        <Link
                                            key={item.id}
                                            to={notificationLink(item)}
                                            className="text-reset notification-item d-block px-3 py-2 border-bottom text-decoration-none"
                                        >
                                            <div className="d-flex">
                                                <div className="flex-grow-1">
                                                    <h6 className="mb-1 font-size-13">{item.title}</h6>
                                                    <div className="font-size-12 text-muted">
                                                        <p className="mb-1 text-truncate">{item.message}</p>
                                                        <p className="mb-0">
                                                            <Clock {...MODERN_ICON.xs} />{" "}
                                                            {formatTimeAgo(item.timestamp)}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </Link>
                                    ))
                                )}
                            </div>
                            <div className="p-2 border-top d-grid">
                                <Link
                                    to={constant.Authedcomponent.notificationsCenter.url}
                                    className="btn btn-sm btn-link font-size-14 text-center text-decoration-none"
                                >
                                    <ArrowCircleRight {...MODERN_ICON.sm} className="me-1" />
                                    <span key="t-view-more">View more</span>
                                </Link>
                            </div>
                        </div>
                    </div>

                    {/* ── User Profile ── */}
                    <div className="dropdown d-inline-block">
                        <button
                            type="button"
                            className="btn header-item user-header-item waves-effect"
                            id="page-header-user-dropdown"
                            data-bs-toggle="dropdown"
                            aria-haspopup="true"
                            aria-expanded="false"
                        >
                            <img className="rounded-circle header-profile-user" src="/img/crew.jpg" alt="Profile photo" />
                            <span className="d-none d-xl-inline-block ms-1" key="t-henry" style={{ color: "#2a5a7b" }}>
                                {user?.full_name} <span className='designation '>{user?.designation?.designation_name}</span>
                            </span>
                            <CaretDown {...MODERN_ICON.sm} className="d-none d-xl-inline-block" />
                        </button>
                        <div className="dropdown-menu dropdown-menu-end">
                            <Link className="dropdown-item d-block" to={constant.Authedcomponent.crewProfile.url}>
                                <UserCircle {...MODERN_ICON.sm} className="align-middle me-1" />{" "}
                                <span key="t-settings">Profile</span>
                            </Link>
                            <div className="dropdown-divider" />
                            <a className="dropdown-item text-danger" href={constant.commoncomponent.logout.url}>
                                <SignOut {...MODERN_ICON.sm} className="align-middle me-1 text-danger" />{" "}
                                <span key="t-logout">Logout</span>
                            </a>
                        </div>
                    </div>

                </div>
            </div>
        </header>
    )
}

export default Header
